/**
 * The message template and the one substitution it makes.
 *
 * `%promo%` is replaced at **every** occurrence, not the first — a template
 * reading "Your code %promo%. Show %promo% at the till." is a reasonable thing
 * to write, and replacing one of them would send a message with the marker
 * still in it (PLAN §6).
 *
 * Nothing is escaped here. XML escaping happens once, at the SMSPro boundary,
 * where it is a property of the transport rather than of the message; escaping
 * it earlier would put `&amp;` in the recipient's SMS.
 *
 * Pure by construction (PLAN §4): the browser previews with this code and the
 * server sends what this code produced.
 */

export const PROMO_PLACEHOLDER = '%promo%';

/**
 * A hard provider limit from `docs/smspro-api.txt`, which the project
 * specification never mentions. At 70 characters per Cyrillic segment it is
 * about twelve SMS, so it is not a limit anyone reaches by accident — but the
 * provider rejects the whole request rather than truncating, so it is checked
 * here rather than discovered at send time (PLAN §6).
 *
 * Counted in UTF-16 code units: the same measure `sms.ts` uses for UCS-2, and
 * never *under*-counting the characters the provider will see.
 */
export const MAX_MESSAGE_UNITS = 800;

export type TemplateProblem = 'empty' | 'missing-placeholder';

export type TemplateResult = { readonly ok: true } | { readonly ok: false; readonly problem: TemplateProblem };

/**
 * Whether a template can be used at all — checked once, when it is written,
 * rather than once per recipient.
 */
export function checkTemplate(template: string): TemplateResult {
  if (template.trim() === '') return { ok: false, problem: 'empty' };
  if (!template.includes(PROMO_PLACEHOLDER)) {
    // Every recipient would get an identical message, which is not what this
    // program is for. Better refused than sent ten thousand times.
    return { ok: false, problem: 'missing-placeholder' };
  }
  return { ok: true };
}

export function countPlaceholders(template: string): number {
  return template.split(PROMO_PLACEHOLDER).length - 1;
}

/** Trimmed, case preserved (PLAN §6). Codes are pasted, and pasted values carry whitespace. */
export function normalizePromoCode(code: string): string {
  return code.trim();
}

export type MessageProblem = 'empty-code' | 'too-long';

export interface RenderedMessage {
  readonly ok: boolean;
  /** What the recipient would receive. Present even when refused, so the screen can show it. */
  readonly message: string;
  /** UTF-16 code units, against `MAX_MESSAGE_UNITS`. */
  readonly length: number;
  readonly problem?: MessageProblem;
}

/**
 * Substitutes one promo code into the template.
 *
 * The rendered message comes back even when it is refused: §59 is explicit that
 * the administrator must see the truth and decide, not be shown a bare error
 * where the message should be.
 */
export function renderMessage(template: string, promoCode: string): RenderedMessage {
  const code = normalizePromoCode(promoCode);
  // `split`/`join` rather than `replaceAll`: in a replacement string `$&` and
  // `$'` are substitution patterns, so a promo code containing one would be
  // silently rewritten into something else.
  const message = template.split(PROMO_PLACEHOLDER).join(code);
  const length = message.length;

  if (code === '') return { ok: false, message, length, problem: 'empty-code' };
  if (length > MAX_MESSAGE_UNITS) return { ok: false, message, length, problem: 'too-long' };

  return { ok: true, message, length };
}

/**
 * The longest message a template can produce for these codes — what the
 * confirmation screen needs, because one code in ten thousand being longer than
 * the rest is what pushes a campaign over a boundary.
 */
export function longestMessage(template: string, promoCodes: readonly string[]): RenderedMessage {
  let longest: RenderedMessage | undefined;

  for (const code of promoCodes) {
    const candidate = renderMessage(template, code);
    if (longest === undefined || candidate.length > longest.length) longest = candidate;
  }
  // No codes yet: the template with an empty substitution is the honest answer,
  // and it carries `empty-code` so nothing mistakes it for a sendable message.
  return longest ?? renderMessage(template, '');
}
